import { useQuery } from "@tanstack/react-query";
import { fetchShorthandGrammar } from "./quickAddApi";

export const SHORTHAND_GRAMMAR_QUERY_KEY = ["shorthand-grammar"];

const GRAMMAR_STORAGE_KEY = "quick-add-grammar-pack";

const isValidPack = (pack) => !!(pack?.constraints && pack?.shorthands);

let memoryPack;

/** Last known pack, so parsing works instantly while the network revalidates. */
export function readStoredPack() {
  if (isValidPack(memoryPack)) return memoryPack;
  try {
    const raw = localStorage.getItem(GRAMMAR_STORAGE_KEY);
    if (!raw) return undefined;
    const pack = JSON.parse(raw);
    if (!isValidPack(pack)) return undefined;
    memoryPack = pack;
    return pack;
  } catch {
    return undefined;
  }
}

export function storePack(pack) {
  if (!isValidPack(pack)) return;
  memoryPack = pack;
  try {
    localStorage.setItem(GRAMMAR_STORAGE_KEY, JSON.stringify(pack));
  } catch {
    // Storage full/unavailable — in-memory pack still covers this session.
  }
}

const fetchGrammarPack = async () => {
  const res = await fetchShorthandGrammar();
  // Endpoint returns the pack at the top level (not Laravel { data }).
  const pack = res?.data?.constraints ? res.data : res?.data?.data || res?.data;
  if (!isValidPack(pack)) {
    throw new Error("Invalid shorthand grammar response");
  }
  storePack(pack);
  return pack;
};

const QUERY_OPTIONS = {
  queryKey: SHORTHAND_GRAMMAR_QUERY_KEY,
  queryFn: fetchGrammarPack,
  staleTime: Infinity,
  cacheTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

/** Warm the cache before the panel opens so the input is never gated on loading. */
export function prefetchShorthandGrammar(queryClient) {
  return queryClient.prefetchQuery(QUERY_OPTIONS);
}

/**
 * Fetches GET /shorthand-grammar once per session (long staleTime).
 * Response is a flat pack: { version, constraints, shorthands, ... }.
 * A copy is persisted in localStorage and served as placeholder data, so
 * parsing works immediately on repeat visits while the fetch revalidates.
 */
export default function useShorthandGrammar(enabled = true) {
  return useQuery({
    ...QUERY_OPTIONS,
    enabled,
    // placeholderData (not initialData): shown while fetching, never marked
    // fresh — the network response always replaces it and re-syncs storage.
    placeholderData: readStoredPack,
  });
}
